import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import isFunction from 'lodash/isFunction';
import MuiAccordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import { Divider } from '@material-ui/core';
import { DropdownIcon } from 'sharedComponents/Icons';
import cx from 'classnames';

import Typography from '@material-ui/core/Typography';

const Accordion = ({
  classes,
  items,
  activeItemHandler,
  onAnimationFinished,
  unpaddedContent,
  isSummaryMagnified,
  isMultipleOpenTabsAllowed,
  activeTabNamesRef,
  isForSearchPage,
}) => {
  const getInitiallyExpandedPanelNames = () => {
    const initiallyExpandedPanelNames = items
      .filter(({ expandedByDefault }) => expandedByDefault)
      .map(({ name }) => name);
    return new Set(initiallyExpandedPanelNames);
  };

  const [expandedPanelNames, setExpandedPanelsNames] = React.useState(getInitiallyExpandedPanelNames);

  React.useEffect(() => {
    if (activeTabNamesRef) {
      // eslint-disable-next-line no-param-reassign
      activeTabNamesRef.current = expandedPanelNames;
    }
  }, []);

  const getUpdatedPanelsSelection = (name) => {
    let updatedPanelsSelection;
    if (isMultipleOpenTabsAllowed) {
      updatedPanelsSelection = new Set([...expandedPanelNames]);
      if (updatedPanelsSelection.has(name)) {
        updatedPanelsSelection.delete(name);
      } else {
        updatedPanelsSelection.add(name);
      }
    } else {
      updatedPanelsSelection = new Set([name]);
    }
    return updatedPanelsSelection;
  };

  const handleChange = (name, alwaysOpen, index) => () => {
    if (alwaysOpen) return;
    const newState = getUpdatedPanelsSelection(name);
    setExpandedPanelsNames(newState);
    if (isFunction(activeItemHandler)) {
      activeItemHandler(index);
    }

    if (activeTabNamesRef) {
      // eslint-disable-next-line no-param-reassign
      activeTabNamesRef.current = newState;
    }
  };

  const TransitionProps = {
    onEntered: () => {
      if (isFunction(onAnimationFinished)) {
        onAnimationFinished();
      }
    },
  };

  const renderContent = (content) => (typeof content === 'string' ? <Typography>{content}</Typography> : content);

  return (
    <>
      {!!items?.length &&
        items.map(({ name, alwaysOpen, isPanelDisabled = false, content }, index) => {
          return (
            <Fragment key={name}>
              {isForSearchPage && <Divider />}
              <MuiAccordion
                expanded={alwaysOpen || expandedPanelNames.has(name)}
                onChange={handleChange(name, alwaysOpen, index)}
                TransitionProps={TransitionProps}
                classes={{
                  root: classes.accordionItem,
                }}
                disabled={isPanelDisabled}
              >
                <AccordionSummary
                  expandIcon={alwaysOpen ? null : <DropdownIcon className={classes.dropdownIcon} />}
                  aria-controls={index}
                  id={index}
                  classes={{
                    root: cx(classes.accordionSummary, isSummaryMagnified && classes.magnifiedSummary),
                    content: cx(classes.summaryContent, unpaddedContent && classes.unpaddedSummaryContent),
                  }}
                >
                  <Typography
                    variant="body2"
                    className={cx(
                      isSummaryMagnified && classes.magnifiedSummaryBody,
                      isForSearchPage && classes.isForSearchPageTitle,
                    )}
                  >
                    {name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  classes={{
                    root: cx(classes.accordionContent, isSummaryMagnified && classes.magnifiedSummaryAccordionContent),
                  }}
                >
                  {renderContent(content)}
                </AccordionDetails>
              </MuiAccordion>
            </Fragment>
          );
        })}
    </>
  );
};

Accordion.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  /** List of items that will be rendered */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
      alwaysOpen: PropTypes.bool,
    }),
  ),
  activeItemHandler: PropTypes.func,
  onAnimationFinished: PropTypes.func,
  unpaddedContent: PropTypes.bool,
  isSummaryMagnified: PropTypes.bool,
  isMultipleOpenTabsAllowed: PropTypes.bool,
  activeTabNamesRef: PropTypes.shape({ current: PropTypes.instanceOf(Set) }),
  isForSearchPage: PropTypes.bool,
};

Accordion.defaultProps = {
  items: [],
  activeItemHandler: () => null,
  unpaddedContent: false,
  isMultipleOpenTabsAllowed: false,
  isForSearchPage: false,
  classes: {},
};

export default Accordion;
